import { useState, useEffect } from "react";

import {
  ArrowButton,
  Badge,
  InfoPanel,
  ListPanel,
  SelectableListItem,
  TextField,
  Tooltip,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as ContactIcon } from "@king-ict/eupisi-ui/components/Icon/Contact.svg";
import { ReactComponent as CriterionIcon } from "@king-ict/eupisi-ui/components/Icon/Criterion.svg";
import { ReactComponent as InstitutionIcon } from "@king-ict/eupisi-ui/components/Icon/Institution.svg";
import { ReactComponent as UsersProgramIcon } from "@king-ict/eupisi-ui/components/Icon/UsersProgram.svg";
import { getInstitutionDisplayNameAndTown } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useLocation } from "react-router";

import {
  getLocationPrograms as getLocationProgramsApi,
  getInstitutionDetails as getInstitutionDetalisApi,
} from "../../api/kindergartenApi";
import InstitutionDetails from "../../models/Institution/InstitutionDetails";
import InstitutionProgramListItem from "../../models/Institution/InstitutionProgramListItem";
import { RequestInstitutionProgramFilters } from "../../models/Institution/InstitutionProgramSearch";
import { WorkOrganizationEnum } from "../../models/Institution/WorkOrganizationEnum";
import { institutions } from "../../routes/routes";
import { InstitutionLocationsAndProgramsFilters } from "./InstitutionLocationsAndProgramsFilters";
import styles from "./InstitutionLocationsAndProgramsPage.module.scss";

export default function InstitutionLocationsAndProgramsPage() {
  const { pedagogicalYearInstitutionId } = useParams<{
    pedagogicalYearInstitutionId: string;
  }>();

  const [filters, setFilters] = useState<RequestInstitutionProgramFilters>(
    {} as RequestInstitutionProgramFilters
  );

  const [
    currentFilters,
    setCurrentFilters,
  ] = useState<RequestInstitutionProgramFilters>(
    {} as RequestInstitutionProgramFilters
  );

  const [locationPrograms, setLocationPrograms] = useState<
    InstitutionProgramListItem[]
  >([]);

  const [selectedInstitutionId, setSelectedInstitutionId] = useState<number>();

  const [resetSelectedItem, setResetSelectedItem] = useState<
    boolean | undefined
  >(false);

  const [
    selectedInstitution,
    setSelectedInstitution,
  ] = useState<InstitutionDetails>();

  const [
    selectedProgram,
    setSelectedProgram,
  ] = useState<InstitutionProgramListItem>();

  const { t } = useTranslation();
  const history = useHistory();

  const emptyValue = "-";

  useEffect(() => {
    (async () => {
      var locationPrograms = await getLocationProgramsApi(
        +pedagogicalYearInstitutionId,
        currentFilters.parentIntitutionId,
        currentFilters.programAgeId,
        currentFilters.programDurationId,
        currentFilters.programId
      );
      setLocationPrograms(locationPrograms);
    })();
  }, [currentFilters, pedagogicalYearInstitutionId]);

  const handleLocationItemClick = (e: any) => {
    const { value, isSelected } = e.target;
    if (value !== selectedInstitutionId) {
      setResetSelectedItem(true);
      setSelectedProgram(undefined);
    }
    if (isSelected) {
      setSelectedInstitutionId(value);
    } else {
      setSelectedInstitutionId(undefined);
    }
  };

  useEffect(() => {
    (async () => {
      var institution = await getInstitutionDetalisApi(
        +pedagogicalYearInstitutionId
      );
      setSelectedInstitution(institution);
    })();
  }, [pedagogicalYearInstitutionId]);

  const handleSearch = async () => {
    setCurrentFilters(filters);
  };

  const handleProgramItemClick = (e: any) => {
    const { value, isSelected } = e.target;
    if (isSelected) {
      const selectedProgram = locationPrograms.find(
        (lp) => lp.pedagogicalYearInstitutionProgramDetailId === +value
      );
      setSelectedProgram(selectedProgram);
    } else {
      setSelectedProgram(undefined);
    }
    setResetSelectedItem(false);
  };
  const location = useLocation();
  const handleGoToInstitutionsPage = () => {
    const fromInstitutionsPage =
      location.state && (location.state as any).fromInstitutionsPage;

    if (fromInstitutionsPage) {
      history.goBack();
    } else {
      history.push(institutions());
    }
  };

  const getInstitutions = () => {
    if (locationPrograms && locationPrograms.length > 0) {
      let institutions = [] as InstitutionProgramListItem[];
      locationPrograms.forEach((institution) => {
        if (
          institutions.length === 0 ||
          institutions.find(
            (i) => i.institutionId === institution.institutionId
          ) == null
        ) {
          institutions.push(institution);
        }
      });

      return institutions.map((institution) => {
        return (
          <SelectableListItem
            selfUnselectable={false}
            key={institution.pedagogicalYearInstitutionProgramDetailId}
            text={getInstitutionDisplayNameAndTown(
              institution.institutionName,
              institution.institutionTown
            )}
            helperText={`${institution.institutionAddress}, ${institution.institutionTown}`}
            value={institution.institutionId}
            onClick={handleLocationItemClick}
          />
        );
      });
    }
  };
  const getPrograms = () => {
    if (
      locationPrograms &&
      locationPrograms.length > 0 &&
      selectedInstitutionId
    ) {
      const programs = locationPrograms.filter(
        (ip) => ip.institutionId === selectedInstitutionId
      );
      return programs.map((program) => {
        return (
          <SelectableListItem
            key={`${program.pedagogicalYearInstitutionProgramDetailId}`}
            text={program.programName}
            helperText={`${program.programLanguage}`}
            value={program.pedagogicalYearInstitutionProgramDetailId}
            onClick={handleProgramItemClick}
          />
        );
      });
    }
  };

  const getProgramDescription = () => {
    if (selectedProgram !== undefined) {
      return (
        <>
          <Row>
            <Col md={12} className={styles.descriptionSubTitles}>
              <IconWrapper color="body-color">
                <InstitutionIcon />
              </IconWrapper>
              <Typography variant="mediumBold" component="h3">
                {`${t("institutionSearchPage.about")}`}
              </Typography>
            </Col>
          </Row>
          <Row className={styles.descriptionItemRow}>
            <Col md={12}>
              <Typography color="light">
                {selectedProgram?.institutionDescription}
              </Typography>
            </Col>
          </Row>
          <Row>
            <Col md={12} className={styles.descriptionSubTitles}>
              <IconWrapper color="body-color">
                <UsersProgramIcon />
              </IconWrapper>
              <Typography variant="mediumBold" component="h3">
                {`${t("common.program")}`}
              </Typography>
            </Col>
          </Row>
          <Row className={styles.descriptionItemRow}>
            <Col xs={6}>
              <TextField
                label={t("institutionProgram.workOrganization")}
                value={`${selectedProgram?.workOrganizationName}`}
                plaintext
                bold
                smallLabel
              />
            </Col>
            <Col xs={6}>
              <TextField
                label={t("institutionDetails.description")}
                value={selectedProgram?.programDescription || emptyValue}
                plaintext
                bold
                smallLabel
              />
            </Col>
            <Col xs={6}>
              <TextField
                label={t("institutionProgram.capacity")}
                value={selectedProgram?.capacity}
                plaintext
                bold
                smallLabel
              />
            </Col>
            <Col xs={6} className={styles.confirmationTextColor}>
              <TextField
                label={t("institutionProgram.economyPrice")}
                value={t("institutionDetails.economyPrice", {
                  priceEUR: selectedProgram?.priceEUR.toFixed(2),
                  priceHRK: selectedProgram?.priceHRK.toFixed(2),
                }).toString()}
                plaintext
                smallLabel
              />
            </Col>
            <Col xs={6}>
              <TextField
                label={t("institutionProgram.firstShift")}
                value={`${selectedProgram?.firstShiftFrom} - ${selectedProgram?.firstShiftTo}`}
                plaintext
                bold
                smallLabel
              />
            </Col>
            {selectedProgram?.workOrganizationId ===
              WorkOrganizationEnum.Shifts && (
              <Col xs={6}>
                <TextField
                  label={t("institutionProgram.secondShift")}
                  value={`${selectedProgram?.secondShiftFrom} - ${selectedProgram?.secondShiftTo}`}
                  plaintext
                  bold
                  smallLabel
                />
              </Col>
            )}
            <Col xs={6}>
              <TextField
                label={t("institutionDetails.note")}
                value={selectedProgram?.programNote || emptyValue}
                plaintext
                bold
                smallLabel
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className={styles.descriptionSubTitles}>
              <IconWrapper color="body-color">
                <ContactIcon />
              </IconWrapper>
              <Typography variant="mediumBold" component="h3">
                {`${t("institutionSearchPage.contact")}`}
              </Typography>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <TextField
                label={t("institutionProgram.address")}
                value={`${selectedProgram?.institutionAddress}, ${selectedProgram?.institutionTown}`}
                plaintext
                bold
                smallLabel
              />
            </Col>
            <Col xs={6}>
              <TextField
                label={t("common.phone")}
                value={`${selectedProgram?.phone}`}
                plaintext
                bold
                smallLabel
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <InfoPanel
                message={t("institutionDetails.exchangeRate", {
                  exchangeRate: selectedProgram?.exchangeRate,
                })}
                color="warning"
                fullWidth
              />
            </Col>
          </Row>
        </>
      );
    }
  };

  return (
    <>
      <Row className={styles.subHeader}>
        <Col xs={12} md={3}>
          <div className={styles.institutionNameInfoSection}>
            <ArrowButton
              ariaLabel={t("accessibility.backButton")}
              backwards
              onClick={handleGoToInstitutionsPage}
            />
            <Typography variant="mediumBold" component="h3">
              {selectedInstitution?.name}
            </Typography>
          </div>
        </Col>
        <Col xs={12} md={3} className={styles.institutionInfo}>
          <Tooltip
            placement="bottom-start"
            overlay={
              <div className={styles.institutionTooltipInfo}>
                <Row>
                  <Col xs={12}>
                    <Typography variant="smallHeading" component="h4">
                      {t("request.mainInstitutionInfo")}
                    </Typography>
                  </Col>
                </Row>
                <Row>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      label={t("common.address")}
                      value={selectedInstitution?.address}
                    />
                  </Col>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      type="text"
                      label={t("common.phone")}
                      value={selectedInstitution?.phone}
                    />
                  </Col>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      type="email"
                      label={t("common.email")}
                      value={selectedInstitution?.email}
                      color="primary"
                    />
                  </Col>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      type="url"
                      label={t("common.webAddress")}
                      value={selectedInstitution?.webAddress}
                      color="primary"
                    />
                  </Col>
                </Row>
                <Row className={styles.rowSpacing}>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      label={t("institutionSearchPage.founderType")}
                      value={selectedInstitution?.founderType}
                    />
                  </Col>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      type="text"
                      label={t("institutionSearchPage.institutionType")}
                      value={selectedInstitution?.institutionType}
                    />
                  </Col>
                  <Col xs={3}>
                    <TextField
                      plaintext
                      bold
                      type="text"
                      label={t("institutionDetails.description")}
                      value={selectedInstitution?.description}
                    />
                  </Col>
                </Row>
              </div>
            }
          >
            <div className={styles.infoSection}>
              <IconWrapper color="primary">
                <InstitutionIcon />
              </IconWrapper>
              <Typography variant="mediumBold" component="h3" color="primary">
                {t("institutionDetails.detailsTitle")}
              </Typography>
            </div>
          </Tooltip>
        </Col>
        <Col xs={12} md={6} className={styles.institutionInfo}>
          <Tooltip
            placement="bottom"
            overlay={
              <div className={styles.institutionCriteriaTooltipInfo}>
                <Row>
                  <Col xs={12}>
                    <Typography variant="smallHeading" component="h4">
                      {t("request.criteriaAndPoints")}
                    </Typography>
                  </Col>
                </Row>
                {selectedInstitution?.criteria.map((criterion, index) => {
                  return (
                    <div
                      key={`${selectedInstitution?.pedagogicalYearInstitutionId}_criterionHeaderTooltip_${index}`}
                      className={styles.criterionToolTipRow}
                    >
                      {criterion.isDirectEnrollment ? (
                        ""
                      ) : (
                        <Badge value={criterion.points} color="grey"></Badge>
                      )}
                      <Typography color="light">
                        {criterion.isDirectEnrollment
                          ? "(" + t("criteriaStep.directEnrollment") + ") "
                          : ""}
                        {criterion.name}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            }
          >
            <div className={styles.criterionSection}>
              <IconWrapper color="primary">
                <CriterionIcon />
              </IconWrapper>
              <Typography variant="mediumBold" component="h3" color="primary">
                {t("request.criteriaAndPoints")}
              </Typography>
            </div>
          </Tooltip>
        </Col>
      </Row>
      <Row className={styles.subHeader}>
        <Col md={12}>
          <InfoPanel
            message={t("institutionDetails.mainInfoMessage")}
            color="success"
            fullWidth
          />
        </Col>
      </Row>
      <InstitutionLocationsAndProgramsFilters
        handleSearch={handleSearch}
        filters={filters}
        setFilters={setFilters}
        pedagogicalYearInstitutionId={+pedagogicalYearInstitutionId}
      ></InstitutionLocationsAndProgramsFilters>
      <Row>
        <Col xs={12} md={3}>
          <ListPanel
            title={t("institutionProgramDataStep.institutionSelection")}
            unselectable={false}
          >
            {getInstitutions()}
          </ListPanel>
        </Col>
        <Col xs={12} md={3}>
          <ListPanel
            title={t("institutionProgramDataStep.programSelection")}
            unselectable={false}
            resetSelectedValue={resetSelectedItem}
          >
            {getPrograms()}
          </ListPanel>
        </Col>
        <Col xs={12} md={6}>
          <div>
            <Row>
              <Col md={12}>
                <Typography
                  variant="smallHeading"
                  className={styles.programDescriptionTitle}
                >
                  {`${t("institutionDetails.description")}`}
                </Typography>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className={styles.programDescriptionContent}>
                  {getProgramDescription()}
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </>
  );
}
